/**
 * Three-way panel-layout model for the agent-first project page (OR-6123 F3).
 *
 * The project page has two visible panes — the Research Assistant chat (left)
 * and the artifact content outlet (right). The header's 3-way toggle drives a
 * single logical layout value:
 *
 *   - `split`          — both panes visible (default)
 *   - `ra-only`        — chat full-width, content collapsed
 *   - `artifacts-only` — content full-width, chat hidden
 *
 * Rather than store a parallel enum that can desync, the layout is **derived**
 * from the two levers that already exist on the page:
 *   - RA visibility   (`showChat` — research-assistant store open + enabled)
 *   - content collapse (`contentCollapsed` — local page state)
 *
 * The fourth combination (chat hidden AND content collapsed → nothing visible)
 * is illegal; {@link deriveProjectPanelLayout} never returns it and
 * {@link projectPanelLayoutLevers} never produces it.
 *
 * The fully-polished collapse-to-strip visuals + persistence are F4 — this
 * model only carries the logical state + the show/hide levers.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export type ProjectPanelLayout = 'split' | 'ra-only' | 'artifacts-only';

export interface ProjectPanelLayoutLevers {
  /** Whether the RA chat pane should be shown. */
  showChat: boolean;
  /** Whether the content (artifacts) pane should be collapsed. */
  contentCollapsed: boolean;
}

/**
 * Derive the active layout segment from the two page levers.
 *
 * - chat hidden            → `artifacts-only` (content is always shown when
 *                            chat is hidden — the illegal "both hidden" combo
 *                            collapses here).
 * - chat shown + collapsed → `ra-only`
 * - chat shown + expanded  → `split`
 */
export const deriveProjectPanelLayout = ({
  showChat,
  contentCollapsed,
}: ProjectPanelLayoutLevers): ProjectPanelLayout => {
  if (!showChat) return 'artifacts-only';
  if (contentCollapsed) return 'ra-only';
  return 'split';
};

/**
 * Map a target layout back to the two levers the page applies. The page wires
 * `showChat` to the research-assistant store (open/close) and
 * `contentCollapsed` to local state.
 */
export const projectPanelLayoutLevers = (layout: ProjectPanelLayout): ProjectPanelLayoutLevers => {
  switch (layout) {
    case 'split':
      return { showChat: true, contentCollapsed: false };
    case 'ra-only':
      return { showChat: true, contentCollapsed: true };
    case 'artifacts-only':
      return { showChat: false, contentCollapsed: false };
  }
};
